import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';
import SwitchWidget from '../../SwitchWidget.js';

//elements
import { NumberInput } from '~/elements/Input.js';
import InfoBox from '~/elements/InfoBox.js';

//utils
import { OVERTIME_PAID_HOURS_HARD_LIMIT_MINS, OVERTIME_PAID_HOURS_SOFT_LIMIT_MINS } from '~/utils/constants.js';

class OvertimeBankSettings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            overtimeBankSettings: {
                allowOvertimeBank: false,
                allowPaidOvertimeBalanceManagement: false,
                enablePaidOvertimeBalanceForAllEmployees: false,
                allowPmToManagePaidOvertimeBalance: false,
                defaultPaidOvertimeBalance: 0,
                autoOvertimeBankRenewal: false,
                autoPayoutMandatoryOvertime: false,
                autoPayoutUrgentWorkOvertime: false
            }
        }
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = () => {
        const { currentEntity } = this.props.clientStore;
        const { overtimeBankSettings } = this.state;
        if (currentEntity.client?.data?.basicRules?.overtimeBankSettings) {
            const _overtimeBankSettings = Object.assign({}, currentEntity.client.data.basicRules.overtimeBankSettings);
            overtimeBankSettings.allowOvertimeBank = _overtimeBankSettings.allowOvertimeBank ? _overtimeBankSettings.allowOvertimeBank : false;
            overtimeBankSettings.allowPaidOvertimeBalanceManagement = _overtimeBankSettings.allowPaidOvertimeBalanceManagement ? _overtimeBankSettings.allowPaidOvertimeBalanceManagement : false;
            overtimeBankSettings.enablePaidOvertimeBalanceForAllEmployees = _overtimeBankSettings.enablePaidOvertimeBalanceForAllEmployees ? _overtimeBankSettings.enablePaidOvertimeBalanceForAllEmployees : false;
            overtimeBankSettings.allowPmToManagePaidOvertimeBalance = _overtimeBankSettings.allowPmToManagePaidOvertimeBalance ? _overtimeBankSettings.allowPmToManagePaidOvertimeBalance : false;
            overtimeBankSettings.defaultPaidOvertimeBalance = _overtimeBankSettings.defaultPaidOvertimeBalance ? _overtimeBankSettings.defaultPaidOvertimeBalance : 0;
            overtimeBankSettings.autoOvertimeBankRenewal = _overtimeBankSettings.autoOvertimeBankRenewal ? _overtimeBankSettings.autoOvertimeBankRenewal : false;
            overtimeBankSettings.autoPayoutMandatoryOvertime = _overtimeBankSettings.autoPayoutMandatoryOvertime ? _overtimeBankSettings.autoPayoutMandatoryOvertime : false;
            overtimeBankSettings.autoPayoutUrgentWorkOvertime = _overtimeBankSettings.autoPayoutUrgentWorkOvertime ? _overtimeBankSettings.autoPayoutUrgentWorkOvertime : false;
        }
        this.setState({ overtimeBankSettings });
    }

    handleCheck = (name, evt) => {
        const { overtimeBankSettings } = this.state;

        if (name === 'allowOvertimeBank' && !evt) {
            overtimeBankSettings.allowPaidOvertimeBalanceManagement = false;
            overtimeBankSettings.enablePaidOvertimeBalanceForAllEmployees = false;
            overtimeBankSettings.defaultPaidOvertimeBalance = 0;
            overtimeBankSettings.autoOvertimeBankRenewal = false;
            overtimeBankSettings.autoPayoutMandatoryOvertime = false;
            overtimeBankSettings.autoPayoutUrgentWorkOvertime = false;
        }

        if (name === 'allowPaidOvertimeBalanceManagement' && !evt) {
            overtimeBankSettings.enablePaidOvertimeBalanceForAllEmployees = false;
            overtimeBankSettings.allowPmToManagePaidOvertimeBalance = false;
            overtimeBankSettings.defaultPaidOvertimeBalance = 0;
            overtimeBankSettings.autoOvertimeBankRenewal = false;
        }

        overtimeBankSettings[name] = evt;
        this.props.handleUserDataChange('overtimeBankSettings', overtimeBankSettings);
        this.setState({ overtimeBankSettings });
    }

    handleBalanceHoursChange = (value) => {
        const { overtimeBankSettings } = this.state;
        const hours = Math.max(0, parseInt(value, 10) || 0);
        const minutesPart = (overtimeBankSettings.defaultPaidOvertimeBalance || 0) % 60;
        this.handleCheck('defaultPaidOvertimeBalance', hours * 60 + minutesPart);
    }

    handleBalanceMinutesChange = (value) => {
        const { overtimeBankSettings } = this.state;
        const minutes = Math.min(59, Math.max(0, parseInt(value, 10) || 0));
        const hoursPart = Math.floor((overtimeBankSettings.defaultPaidOvertimeBalance || 0) / 60);
        this.handleCheck('defaultPaidOvertimeBalance', hoursPart * 60 + minutes);
    }

    render() {
        const { overtimeBankSettings } = this.state;
        const { t } = this.props;
        const balanceHours = Math.floor((overtimeBankSettings.defaultPaidOvertimeBalance || 0) / 60);
        const balanceMinutes = (overtimeBankSettings.defaultPaidOvertimeBalance || 0) % 60;
        // The two auto-payout switches below only make sense once their "parent" feature can
        // actually produce that overtime type in the first place - allowManagingTimelogsManually
        // is what lets employees log mandatory overtime/flexitime at all (see General.js's "Allow
        // employees to add mandatory overtime/flexitime on timesheets"), and urgentWorkSettings.
        // allowUrgentWork is what lets urgent work timesheets exist (see UrgentWorkSettings.js's
        // "Allow Urgent Work").
        const clientBasicRules = this.props.clientStore.currentEntity.client?.data?.basicRules || {};
        const mandatoryOvertimeAllowed = !!clientBasicRules.allowManagingTimelogsManually;
        const urgentWorkAllowed = !!clientBasicRules.urgentWorkSettings?.allowUrgentWork;

        return (
            <div className='w-100 mt-2'>
                <Row>
                    <Col xs={6}>
                        <Row className='mt-2'>
                            <label className="control-label font-size-15 mt-2 font-color-primary">{t('Overtime bank rules')}</label>
                        </Row>
                        <hr />
                        <Row className='align-items-center mb-3'>
                            <Col xs={10}>
                                <label className="control-label">{t('Store overtime hours in Overtime Bank')}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-allow-overtime-bank">
                                                {t('Enabling this will give access to use the Overtime Bank feature and store overtime hours in the Overtime Bank balance')}
                                            </Tooltip>
                                        }>
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={2}>
                                <SwitchWidget
                                    value={overtimeBankSettings.allowOvertimeBank}
                                    onChange={checked => this.handleCheck('allowOvertimeBank', checked)}
                                />
                            </Col>
                        </Row>
                        <Row className='align-items-center mb-3'>
                            <Col xs={10}>
                                <label className="control-label">{t('Allow paid overtime balance management')} ({t("Default - Fixed salaried emmployees")})
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-allow-paid-overtime-balance-management">
                                                {t('Allow Admin/PMs to manually manage paid overtime for the employees')}
                                            </Tooltip>
                                        }>
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={2}>
                                <SwitchWidget
                                    value={overtimeBankSettings.allowPaidOvertimeBalanceManagement}
                                    disabled={!overtimeBankSettings.allowOvertimeBank}
                                    onChange={checked => this.handleCheck('allowPaidOvertimeBalanceManagement', checked)}
                                />
                            </Col>
                        </Row>
                        <Row className='align-items-center mb-3'>
                            <Col xs={10}>
                                <label className="control-label">{t('Enable paid overtime balance for all employees')}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-enable-paid-overtime-balance-all-employees">
                                                {t('Allow Admin/PMs to manage paid overtime balance for all employees, please use the overtieme bank to add overtime balance for all employees')}
                                            </Tooltip>
                                        }>
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={2}>
                                <SwitchWidget
                                    value={overtimeBankSettings.enablePaidOvertimeBalanceForAllEmployees}
                                    disabled={!overtimeBankSettings.allowPaidOvertimeBalanceManagement}
                                    onChange={checked => this.handleCheck('enablePaidOvertimeBalanceForAllEmployees', checked)}
                                />
                            </Col>
                        </Row>
                        {overtimeBankSettings.enablePaidOvertimeBalanceForAllEmployees &&
                            <Row className='mt-2'>
                                <Col xs={12} className='d-flex mx-1'>
                                    <span className='fs12 fw-bold text-danger me-2'>{t("Note")}:</span>
                                    <i className='fs12  text-muted'>
                                        {t('By enabling this rule, overtime paid balance will be available for all employees. Please add the overtime balance from the Overtime Bank for all employees')}.
                                    </i>
                                </Col>
                            </Row>
                        }
                        <Row className='align-items-center mb-3'>
                            <Col xs={10}>
                                <label className="control-label">{t('Allow PMs to manage paid overtime balance')}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-allow-pm-manage-paid-overtime-balance">
                                                {t('When off, only Admins can manually add/subtract paid overtime balance — PMs will not see the option.')}
                                            </Tooltip>
                                        }>
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={2}>
                                <SwitchWidget
                                    value={overtimeBankSettings.allowPmToManagePaidOvertimeBalance}
                                    disabled={!overtimeBankSettings.allowPaidOvertimeBalanceManagement}
                                    onChange={checked => this.handleCheck('allowPmToManagePaidOvertimeBalance', checked)}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={6}>
                        <Row className='mt-2'>
                            <label className="control-label font-size-15 mt-2 font-color-primary">{t('Overtime auto payout rules')}</label>
                        </Row>
                        <hr />
                        <Row className='align-items-center mb-3'>
                            <Col xs={10}>
                                <label className="control-label">{t('Mandatory overtime is paid out immediately and adds on to the paid balance')}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-auto-payout-mandatory-overtime">
                                                {t('When enabled, mandatory overtime is paid out immediately and credited to the employee\'s paid overtime balance instead of being logged as regular accrued overtime. Requires "Allow employees to add mandatory overtime/flexitime on timesheets" to be enabled')}
                                            </Tooltip>
                                        }>
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={2}>
                                <SwitchWidget
                                    value={overtimeBankSettings.autoPayoutMandatoryOvertime}
                                    disabled={!overtimeBankSettings.allowOvertimeBank || !mandatoryOvertimeAllowed}
                                    onChange={checked => this.handleCheck('autoPayoutMandatoryOvertime', checked)}
                                />
                            </Col>
                        </Row>
                        <Row className='align-items-center mb-3'>
                            <Col xs={10}>
                                <label className="control-label">{t('Urgent work overtime is paid out immediately and adds on to the paid balance')}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-auto-payout-urgent-work-overtime">
                                                {t('When enabled, urgent work overtime is paid out immediately and credited to the employee\'s paid overtime balance instead of being logged as regular accrued overtime. Requires "Allow Urgent Work" to be enabled')}
                                            </Tooltip>
                                        }>
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={2}>
                                <SwitchWidget
                                    value={overtimeBankSettings.autoPayoutUrgentWorkOvertime}
                                    disabled={!overtimeBankSettings.allowOvertimeBank || !urgentWorkAllowed}
                                    onChange={checked => this.handleCheck('autoPayoutUrgentWorkOvertime', checked)}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
                {overtimeBankSettings.allowOvertimeBank && overtimeBankSettings.allowPaidOvertimeBalanceManagement &&
                    <Row>
                        <Col xs={6}>
                            <Row className='align-items-center my-3'>
                                <Col xs={10}>
                                    <label className="control-label">{t('Auto-renew paid overtime balance every year')}
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Tooltip id="tooltip-auto-overtime-bank-renewal">
                                                    {t("Each employee's Paid Overtime Renewal hours, set on their profile, is automatically re-credited to their Overtime Bank at the start of every year, replacing the prior year's balance")}
                                                </Tooltip>
                                            }>
                                            <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                        </OverlayTrigger>
                                    </label>
                                </Col>
                                <Col xs={2}>
                                    <SwitchWidget
                                        value={overtimeBankSettings.autoOvertimeBankRenewal}
                                        disabled={!overtimeBankSettings.allowPaidOvertimeBalanceManagement}
                                        onChange={checked => this.handleCheck('autoOvertimeBankRenewal', checked)}
                                    />
                                </Col>
                            </Row>
                            {overtimeBankSettings.autoOvertimeBankRenewal &&
                                <Row className='mt-2 mb-3'>
                                    <Col xs={12} className='d-flex mx-1'>
                                        <span className='fs12 fw-bold text-danger me-2'>{t("Note")}:</span>
                                        <i className='fs12 text-muted'>
                                            {t("Renewal uses each employee's Paid Overtime Renewal hours set on their profile. Employees with no default balance (0 or blank) are skipped and will not be renewed. Nothing carries forward from the prior year - the balance is replaced fresh at the start of each year")}.
                                        </i>
                                    </Col>
                                </Row>
                            }
                            <Row className='mt-3'>
                                <Col xs={12}>
                                    <label className="control-label">{t('Default Paid Overtime Balance')}
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Tooltip id="tooltip-default-paid-overtime-balance">
                                                    {t('Add the default overtime paid balance to be added into to employees Overtime Bank balance')}
                                                </Tooltip>
                                            }>
                                            <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                        </OverlayTrigger>
                                    </label>
                                </Col>
                                <Col xs={3}>
                                    <span className="text-muted fs12">{t('Hours')}(hh)</span>
                                    <NumberInput
                                        width={"50"}
                                        value={balanceHours}
                                        onChange={e => this.handleBalanceHoursChange(e.target.value)}
                                    />
                                </Col>
                                <Col xs={3}>
                                    <span className="text-muted fs12">{t('Minutes')}(mm)</span>
                                    <NumberInput
                                        width={"50"}
                                        value={balanceMinutes}
                                        onChange={e => this.handleBalanceMinutesChange(e.target.value)}
                                    />
                                </Col>
                            </Row>

                        </Col>
                    </Row>
                }
                <hr />
                <Row className='mt-2'>
                    <Col sm={12}>
                        <InfoBox icon="fa fa-info-circle" topic={t('Overtime cycle details')}>
                            <div>{t('Overtime Bank Cycle Length')}: {t('Yearly')} ({t('Calendar year')})</div>
                            <div>{t('Carry Forward')}: {t('None')} - {t('outstanding balance must be settled within the same period')}</div>
                            <div>{t('Maximum Paid Overtime')}: {OVERTIME_PAID_HOURS_HARD_LIMIT_MINS / 60} {t('hours/year')} - {t('submissions above this limit are rejected')}</div>
                            <div>{t('Recommended Paid Overtime Limit')}: {OVERTIME_PAID_HOURS_SOFT_LIMIT_MINS / 60} {t('hours/year')} - {t('submissions above this limit require confirmation')}</div>
                        </InfoBox>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default withLocalization(inject('clientStore')(observer(OvertimeBankSettings)));
